import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import { useSnackbar } from "notistack";
import slugify from "slugify";
import { Autocomplete, Avatar, Box, Button, Link, Typography, Chip, FormControl, Container, InputLabel, Divider, Select, Checkbox, ListItemText, ListSubheader,
  OutlinedInput, MenuItem, Switch, FormControlLabel, Pagination, TextField, CircularProgress } from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import sh from "bundles/common/utils/sh";
import { statusChipColor } from "bundles/common/utils/constants";
import { belgianRegions, frenchRegions, seniorityLevels } from "bundles/common/utils/constants";
import { capitalize, formatRegion, organizeCategoriesByDepartment, formatSalaryInternal, getFormattedDate } from "bundles/common/utils/helpers";
import { duplicateOffer } from "bundles/common/utils/actions/job_offers";
import ConfirmModal from "bundles/Internal/components/ConfirmModal";
import NoData from "internal/no-data.svg";
import CustomExpansionControl from "bundles/Internal/components/partials/CustomExpansionControl";
import ExpandedJobOffer from "bundles/Internal/components/partials/ExpandedJobOffer";

const Jobs = () => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const currentUser = useSelector((state) => state.current_user);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const [jobs, setJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState({ allSelected: false, selectedCount: 0, selectedRows: [] });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState([]);
  const [isArchiveConfirmModalOpen, setIsArchiveConfirmModalOpen] = useState(false);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);

  const [organizedCategories, setOrganizedCategories] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState("online");
  const [onlineSwitch, setOnlineSwitch] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSeniorities, setSelectedSeniorities] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [count, setCount] = useState(0);
  const [showIndeed, setShowIndeed] = useState(false);
  const [showStepstone, setShowStepstone] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);

  const customStyles = {
    headCells: {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:nth-last-of-type(1)": {
          borderLeft: "0.4px solid #DDDDDD",
        },
      }
    },
    cells: {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:nth-last-of-type(1)": {
          borderLeft: "0.4px solid #DDDDDD",
        },
      }
    }
  };

  const isRowExpandable = (row) => {
    return ["online", "archived"].includes(row.offer_status) && row.job_applications?.length > 0;
  };

  const columns = [
    {
      name: "",
      button: true,
      cell: (row) => (
        <CustomExpansionControl
          row={row}
          expandedRows={expandedRows}
          setExpandedRows={setExpandedRows}
          applicationsCount={row.job_applications.length}
          isRowExpandable={() => isRowExpandable(row)}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      width: "56px",
    },
    {
      name: "Job title",
      selector: row => row.job_title,
      grow: 1.5,
      sortable: true,
      sortFunction: (rowA, rowB) => {
        if (rowA.job_title < rowB.job_title) return -1;
        if (rowA.job_title > rowB.job_title) return 1;

        const dateA = new Date(rowA.published_at);
        const dateB = new Date(rowB.published_at);
        return dateB - dateA;
      },
      width: "11vw",
    },
    {
      name: "Company",
      selector: row => row.company?.name || "N/A",
      sortable: true,
      sortFunction: (rowA, rowB) => {
        const nameA = rowA.company?.name?.toLowerCase() || "";
        const nameB = rowB.company?.name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
      width: "7vw",
    },
    {
      name: "City",
      selector: row => row.city,
      sortable: true,
      sortFunction: (rowA, rowB) => {
        if (rowA.city < rowB.city) return -1;
        if (rowA.city > rowB.city) return 1;

        const dateA = new Date(rowA.published_at);
        const dateB = new Date(rowB.published_at);
        return dateB - dateA;
      },
      width: "8vw",
    },
    {
      name: "Category",
      selector: row =>  row.category?.name ? <Chip label={row.category?.name} /> : "",
      sortable: true,
      sortFunction: (rowA, rowB) => {
        if (rowA.category.name < rowB.category.name) return -1;
        if (rowA.category.name > rowB.category.name) return 1;

        const dateA = new Date(rowA.published_at);
        const dateB = new Date(rowB.published_at);
        return dateB - dateA;
      },
      width: "9vw",
    },
    {
      name: "Salary",
      selector: row => {
        if (row.salary_type === "no-salary") {
          return <span style={{ color: "grey" }}>No salary</span>;
        }
        if ((row.min_salary || row.min_salary === 0) && row.max_salary) {
          return formatSalaryInternal(row.salary_type, row.min_salary, row.max_salary);
        }
        return "/";
      },
      sortable: true,
      sortFunction: (rowA, rowB) => {
        const salaryTypePriority = { yearly: 3, monthly: 2, daily: 1, "no-salary": 0 };

        const typePriorityA = salaryTypePriority[rowA.salary_type] || 0;
        const typePriorityB = salaryTypePriority[rowB.salary_type] || 0;

        if (typePriorityA !== typePriorityB) {
          return typePriorityB - typePriorityA;
        }

        if (rowA.min_salary !== rowB.min_salary) {
          return rowB.min_salary - rowA.min_salary;
        }

        const dateA = new Date(rowA.published_at);
        const dateB = new Date(rowB.published_at);
        return dateB - dateA;
      },
      width: "10vw"
    },
    {
      name: "Status",
      selector: row => {
        let value = capitalize(row.offer_status);
        return <Chip color={statusChipColor[row.offer_status] || "default"} label={value} />;
      },
      center: true
    },
    {
      name: "Created by",
      selector: row =>  {
        return (
          <Box style={{ display: "flex", columnGap: "4px", alignItems: "center"}}>
            <Avatar
              sx={{ width: 24, height: 24 }}
              alt={`${row.user?.first_name} ${row.user?.last_name}`}
              src={row.user?.avatar_url}
            />
            <Box>{`${row.user?.first_name} ${row.user?.last_name}`}</Box>
          </Box>
        );
      },
      center: true
    },
    {
      name: "Publication date",
      selector: row => getFormattedDate(row.published_at, false),
      sortable: true,
      sortFunction: (rowA, rowB) => {
        const dateA = new Date(rowA.published_at);
        const dateB = new Date(rowB.published_at);
        return dateA - dateB;
      },
      id: "published_at"
    },
    {
      name: <img src={"/static/images/logos/StepStone.svg"} alt="Stepstone" style={{ height: "12px" }} />,
      selector: row => row.offer_status === "online" ? (
        <Checkbox
          checked={row.on_stepstone}
          color="primary"
          onClick={() => {
            if (row.on_stepstone && row.stepstone_url) {
              window.open(row.stepstone_url, "_blank", "noopener,noreferrer");
            }
          }}
          sx={{
            cursor: row.on_stepstone ? "pointer" : "default",
            pointerEvents: row.on_stepstone ? "auto" : "none",
          }}
        />
      ) : "/",
      center: true,
      width: "80px",
    },
    {
      name: <img src={"/static/images/logos/Indeed.svg"} alt="Indeed" style={{ height: "12px", marginBottom: "2px" }} />,
      selector: row => row.offer_status === "online" ? (
        <Checkbox
          checked={row.on_indeed}
          onChange={(event) => handleIndeedCheckboxChange(event, row)}
          color="primary"
          sx={{ cursor: "pointer" }}
        />
      ) : "/",
      center: true,
      width: "80px",
    },
    {
      name: "",
      selector: row => {
        const slugifyOptions = {
          lower: true,
          strict: true,
          remove: /[*+~.()'"!:@]/g
        };
        const jobTitleSlug = slugify(row.job_title, slugifyOptions);
        const publicURL = window.location.origin;
        const jobUrl = `${publicURL}/jobs/${jobTitleSlug}/${row.id}`;
        return (<Box>
          {row.offer_status == "online" &&
          <>
            <Button
              size="small"
              variant="text"
              onClick={() => window.open(jobUrl, "_blank")}
            >
              → See Job
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => { navigate(`/internal/edit_ad/${row.id}`, { state: { jobOffer: row } }); }}
            >
              Edit
            </Button>
          </>
          }
          {row.offer_status == "archived" &&
            <Button
              size="small"
              variant="text"
              disabled={loading}
              onClick={() => duplicateOffer(row.id, setLoading, authenticityToken, onDuplicateSuccessful, onUpdateFail)}
            >
              Duplicate
            </Button>
          }
        </Box>);
      },
      width: "180px",
      right: true,
    },
  ];

  const fetchEmployees = () => {
    sh.get("internal/all_employees").then((res) => {
      setEmployees(res?.data?.employees || []);
    });
  };

  const fetchCategories = () => {
    sh.get("/categories", {
      authenticity_token: authenticityToken,
    }).then((res) => {
      const fetchedCategories = res?.data?.categories;
      setOrganizedCategories(organizeCategoriesByDepartment(fetchedCategories));
    });
  };

  const onSelectedRowsChange = (params) => {
    setSelectedJobs(params);
  };

  const onDuplicateSuccessful = (newJob) => {
    if (selectedStatus === "all") {
      setJobs(prevJobs => [newJob, ...prevJobs]);
    }
    enqueueSnackbar("Offer successfully duplicated!", {
      variant: "success",
    });
  };

  const onUpdateFail = (error, context) => {
    let errorMessage = "Oops, an error occurred.";

    if (error?.response?.status === 401) {
      if (context === "bulk_archive") {
        errorMessage = "You need admin privileges to archive/delete job offers.";
      } else if (context === "indeed_toggle") {
        errorMessage = "You need admin privileges to modify Indeed settings.";
      } else {
        errorMessage = "You don't have permission to perform this action.";
      }
    } else if (error?.response?.status === 403) {
      errorMessage = "Access denied. You don't have the required permissions.";
    } else if (error?.response?.status === 404) {
      errorMessage = "The requested resource was not found.";
    } else if (error?.response?.status >= 500) {
      errorMessage = "Server error. Please try again later.";
    }

    enqueueSnackbar(errorMessage, { variant: "error" });
  };

  const onBulkArchive = () => {
    setIsArchiveConfirmModalOpen(true);
  };

  const onArchiveConfirmed = () => {
    let archives = selectedJobs.selectedRows.filter((offer) => offer.offer_status != "draft");
    let deletions = selectedJobs.selectedRows.filter((offer) => offer.offer_status == "draft");
    sh.post("/internal/job_offers/bulk_archive", {
      authenticity_token: authenticityToken,
      ids: {
        archives: archives.map((offer) => offer.id),
        deletions: deletions.map((offer) => offer.id)
      }
    }).then(() => {
      fetchAllJobs();
      setClearSelectedRows(true);
      setIsArchiveConfirmModalOpen(false);
      enqueueSnackbar("Offers successfully archived/deleted!", {
        variant: "success",
      });
      setClearSelectedRows(false);
      setSelectedJobs({ allSelected: false, selectedCount: 0, selectedRows: [] });
    }).catch((error) => onUpdateFail(error, "bulk_archive"));
  };

  const handleIndeedCheckboxChange = (event, row) => {
    if (!currentUser?.["admin?"]) {
      enqueueSnackbar("Only admins can modify Indeed settings.", {
        variant: "error",
      });
      return;
    }

    const isChecked = event.target.checked;

    sh.post("/internal/job_offers/indeed_toggle", {
      authenticity_token: authenticityToken,
      job_offer_id: row.id,
      on_indeed: isChecked
    }).then(() => {
      enqueueSnackbar(
        `Job offer ${isChecked ? "pushed to" : "removed from"} Indeed feed!`,
        { variant: "success" }
      );
      setJobs((prevJobs) =>
        prevJobs.map((job) =>
          job.id === row.id ? { ...job, on_indeed: isChecked } : job
        )
      );
    }).catch((error) => {
      onUpdateFail(error, "indeed_toggle");
    });
  };

  const options = {
    selectedCategories,
    selectedSeniorities,
    selectedRegions,
    selectedEmployee,
    selectedStatus,
    showIndeed,
    showStepstone,
    page: currentPage,
  };

  const fetchAllJobs = ({
    page = currentPage,
    selectedCategories,
    selectedSeniorities,
    selectedRegions,
    selectedEmployee,
    selectedStatus,
    showIndeed,
    showStepstone
  } = {}) => {
    setLoading(true);

    const params = {
      authenticity_token: authenticityToken,
      selected_categories: selectedCategories?.map(category => category.id) || [],
      selected_seniorities: selectedSeniorities?.map(seniority => seniority.code) || [],
      selected_regions: selectedRegions,
      selected_employee: selectedEmployee?.id,
      offer_status: selectedStatus,
      indeed_jobs: showIndeed,
      stepstone_jobs: showStepstone,
      page,
      order_by: "published_at"
    };

    sh.get("internal/job_offers", { params })
      .then((res) => {
        setJobs(res?.data?.data);
        setPageCount(res.data?.pagy?.pages);
        setCount(res?.data?.pagy?.count);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchAllJobs({
      ...options,
      page: value
    });
  };

  const handleRegionChange = (selectedRegions) => {
    setCurrentPage(1);
    fetchAllJobs({
      ...options,
      selectedRegions: selectedRegions
    });
    setSelectedRegions(selectedRegions);
  };

  const handleCategoryChange = (selectedItems) => {
    setCurrentPage(1);

    // Separate categories from seniorities
    const categories = selectedItems.filter(item => item.type !== "seniority");
    const seniorities = selectedItems.filter(item => item.type === "seniority");

    // Toggle seniorities: count occurrences and keep odd counts (selected)
    const codeCounts = {};
    seniorities.forEach(seniority => {
      codeCounts[seniority.code] = (codeCounts[seniority.code] || 0) + 1;
    });

    const uniqueSeniorities = Object.keys(codeCounts)
      .filter(code => codeCounts[code] % 2 === 1)
      .map(code => seniorities.find(s => s.code === code));

    setSelectedCategories(categories);
    setSelectedSeniorities(uniqueSeniorities);

    fetchAllJobs({
      ...options,
      selectedCategories: categories,
      selectedSeniorities: uniqueSeniorities
    });
  };



  const handleSelectEmployee = (selectedEmployee) => {
    setCurrentPage(1);
    fetchAllJobs({
      ...options,
      selectedEmployee: selectedEmployee
    });
    setSelectedEmployee(selectedEmployee);
  };

  const onSwitchChange = (event) => {
    const checked = event.target.checked;
    const newStatus = checked ? "online" : "archived";
    setCurrentPage(1);
    setSelectedStatus(newStatus);
    fetchAllJobs({
      ...options,
      selectedStatus: newStatus,
      page: 1
    });
    setOnlineSwitch(checked);
  };

  const handleIndeedSwitchChange = (event) => {
    const isChecked = event.target.checked;
    setShowIndeed(isChecked);
    const newOptions = isChecked ? { ...options, showIndeed: true } : { ...options, showIndeed: false };

    fetchAllJobs(newOptions);
  };

  const handleStepstoneSwitchChange = (event) => {
    const isChecked = event.target.checked;
    setShowStepstone(isChecked);
    const newOptions = isChecked ? { ...options, showStepstone: true } : { ...options, showStepstone: false };

    fetchAllJobs(newOptions);
  };

  const removeAllFilters = () => {
    setCurrentPage(1);
    setSelectedCategories([]);
    setSelectedSeniorities([]);
    setSelectedRegions([]);
    setSelectedEmployee(null);
    setSelectedStatus("online");
    setOnlineSwitch(true);
    setShowIndeed(false);
    setShowStepstone(false);
    fetchAllJobs({
      selectedStatus: "online"
    });
  };

  const redirectUnauthorizedUsed = () => {
    navigate("/internal");
  };


  useEffect(() => {
    if (!currentUser || !currentUser["admin_or_manager?"]) {
      redirectUnauthorizedUsed();
    } else {
      fetchCategories();
      fetchEmployees();
    }
  }, []);

  useEffect(() => {
    const userId = searchParams.get("user_id");
    const onIndeed = searchParams.get("on_indeed");

    if (userId && employees.length > 0) {
      const employee = employees.find(employee => employee.id === parseInt(userId));
      if (employee) {
        setSelectedEmployee(employee);

        const shouldShowIndeed = onIndeed === "true";
        if (shouldShowIndeed) {
          setShowIndeed(true);
        }

        fetchAllJobs({
          ...options,
          selectedEmployee: employee,
          showIndeed: shouldShowIndeed
        });
      }
    } else if (employees.length > 0 && !userId) {
      // Initial load when no URL params
      fetchAllJobs(options);
    }
  }, [employees, searchParams]);

  return (
    <>
      <Container maxWidth="100%" style={{ paddingTop: "32px" }}>
        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center"}}>
          <h1 style={{ fontSize: "24px" }}>
            All Jobs{
              <span style={{ fontSize: "16px", color: "#666", fontWeight: "normal" }}>
                {currentUser["admin?"] ? " (admin access)" :
                  currentUser["manager?"] ? " (in your regional departments)" :
                    null}
              </span>
            }
          </h1>
          <Button variant="squared" color="primaryContained" href="/internal/new_ad">New job offer</Button>
        </Box>
        <Box style={{ display: "flex", justifyContent: "space-between", marginTop: "24px" }}>
          <Button
            size="small"
            variant="squared"
            color="primaryOutlined"
            onClick={onBulkArchive}
            disabled={selectedJobs.selectedCount < 1}
          >
            Archive/Delete{" "}{selectedJobs.selectedCount > 0 ? `(${selectedJobs.selectedCount})` : ""}
          </Button>
          <Box style={{ display: "flex", columnGap: "8px", marginLeft: "16px"}}>
            <FormControlLabel
              control={<Switch checked={onlineSwitch} onChange={onSwitchChange} />}
              label={
                <Box style={{ display: "flex", alignItems: "center" }}>
                  <Typography><strong>{count} {onlineSwitch ? "online" : "archived"} jobs</strong></Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={<Switch checked={showIndeed} onChange={handleIndeedSwitchChange} />}
              label={<img src="/static/images/logos/Indeed.svg" alt="Indeed" style={{ height: "22px", marginBottom: "4px" }} />}
            />
            <FormControlLabel
              control={<Switch checked={showStepstone} onChange={handleStepstoneSwitchChange} />}
              label={<img src="/static/images/logos/StepStone.svg" alt="Stepstone" style={{ height: "22px" }} />}
            />
            <Box>
              <FormControl sx={{ width: "240px", ".MuiOutlinedInput-root": { height: "40px" } }}>
                <InputLabel id="demo-multiple-checkbox-label" sx={{ top: (selectedCategories.length + selectedSeniorities.length) === 0 ? "-6px" : "0" }}>Select Categories</InputLabel>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  value={[...selectedCategories, ...selectedSeniorities]}
                  onChange={(event) => handleCategoryChange(event.target.value)}
                  input={<OutlinedInput label="Select Categories" />}
                  renderValue={(selected) => selected.map(item => {
                    if (item.type === "seniority") {
                      return item.value || item.code;
                    }
                    return item.name || item.value;
                  }).join(", ")}
                >
                  {Object.entries(organizedCategories).map(([department, categories]) => [
                    <ListSubheader key={department}>{department}</ListSubheader>,
                    ...categories.map((category) => (
                      <MenuItem key={category.id} value={category} sx={{ padding: "0px 8px" }}>
                        <Checkbox checked={selectedCategories.includes(category)} />
                        <ListItemText primary={category.name} />
                      </MenuItem>
                    ))
                  ])}
                  {/* Legal Seniorities */}
                  <ListSubheader key="legal-seniorities">Legal Seniorities</ListSubheader>
                  {seniorityLevels.map((seniority) => (
                    <MenuItem key={seniority.code} value={{ code: seniority.code, type: "seniority", value: seniority.value }} sx={{ padding: "0px 8px" }}>
                      <Checkbox checked={selectedSeniorities.some(sen => sen.code === seniority.code && sen.type === "seniority")} />
                      <ListItemText primary={seniority.value} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FormControl sx={{ width: "240px", ".MuiOutlinedInput-root": { height: "40px" } }}>
                <InputLabel id="demo-multiple-checkbox-label" sx={{ top: selectedRegions.length === 0 ? "-6px" : "0" }}>Select Regions</InputLabel>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  value={selectedRegions}
                  onChange={(event) => handleRegionChange(event.target.value)}
                  input={<OutlinedInput label="Select Regions" />}
                  renderValue={(selected) => selected.map(item => formatRegion(item)).join(", ")}
                >
                  <ListSubheader>Belgium</ListSubheader>
                  {belgianRegions.map((name) => (
                    <MenuItem key={name} value={name} sx={{ padding: "0px 8px" }}>
                      <Checkbox checked={selectedRegions.indexOf(name) > -1} />
                      <ListItemText primary={formatRegion(name)} />
                    </MenuItem>
                  ))}
                  <ListSubheader>France</ListSubheader>
                  {frenchRegions.map((name) => (
                    <MenuItem key={name} value={name} sx={{ padding: "0px 8px" }}>
                      <Checkbox checked={selectedRegions.indexOf(name) > -1} />
                      <ListItemText primary={formatRegion(name)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Autocomplete
                size="small"
                options={employees}
                value={selectedEmployee}
                onChange={(params, value) => handleSelectEmployee(value) }
                renderOption={(props, option) => {
                  return (
                    <Box style={{ display: "flex", columnGap: "4px", alignItems: "center"}} {...props} key={`${option.id}${option.last_name}`}>
                      <Avatar
                        sx={{ width: 24, height: 24 }}
                        alt={`${option?.first_name} ${option?.last_name}`}
                        src={option?.avatar_url}
                      />
                      <Box>{`${option?.first_name} ${option?.last_name}`}</Box>
                    </Box>
                  );
                }}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                filterOptions={(options, params) => {
                  let query = params?.inputValue?.toLowerCase() || "";
                  return options.filter((user) => {
                    return user?.first_name?.toLowerCase()?.includes(query) ||
                      user?.last_name?.toLowerCase()?.includes(query) ||
                      user?.regional_department?.name?.toLowerCase()?.includes(query);
                  });
                }}
                sx={{ width: "240px", ".MuiAutocomplete-inputRoot": { height: "40px" } }}
                renderInput={(params) => <TextField {...params} label="Select an employee" />}
                renderTags={() => {}}
              />
            </Box>
            <Box style={{ display: "flex", alignItems: "center", marginRight: "32px" }}>
              <Link onClick={removeAllFilters} sx={{ cursor: "pointer" }}><RestoreIcon/></Link>
            </Box>
          </Box>
        </Box>
        <Divider style={{ marginTop: "8px" }}/>
        <DataTable
          noDataComponent={
            <Box style={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", rowGap: "16px"}}>
              <h3>No jobs created yet</h3>
              <img style={{ width: "45%", marginTop: "64px"}} src={NoData} />
            </Box>
          }
          columns={columns}
          data={jobs}
          expandableRows
          expandableRowsHideExpander={true}
          expandableRowExpanded={(row) => expandedRows.includes(row.id)}
          expandableRowsComponent={({ data }) => <ExpandedJobOffer data={data} isReadOnly={true} />}
          defaultSortAsc={false}
          responsive
          striped
          highlightOnHover
          pointerOnHover
          selectableRows
          progressPending={loading}
          progressComponent={
            <Box sx={{ padding: "36px" }}>
              <CircularProgress />
            </Box>
          }
          onSelectedRowsChange={onSelectedRowsChange}
          clearSelectedRows={clearSelectedRows}
          onRowClicked={(row) => navigate(`/internal/edit_ad/${row.id}`)}
          customStyles={customStyles}
        />
        {!loading &&
          <Box display="flex" justifyContent="center" mt={4} mb={4}>
            <Pagination count={pageCount} page={currentPage} onChange={handlePageChange} />
          </Box>
        }
      </Container>
      <ConfirmModal
        isOpen={isArchiveConfirmModalOpen}
        setIsOpen={setIsArchiveConfirmModalOpen}
        handleConfirm={onArchiveConfirmed}
        label={`You're about to archive/delete ${selectedJobs.selectedCount} offers.`}
      />
    </>
  );
};

export default Jobs;
